// Local-first persistence for personal study tools (zero server cost).
const read = (k, def) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : def;
  } catch {
    return def;
  }
};
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const uid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

// ---- identity (for family sharing) ----
export function getDeviceId() {
  let id = localStorage.getItem("fw_device");
  if (!id) {
    id = uid();
    localStorage.setItem("fw_device", id);
  }
  return id;
}
export const getDisplayName = () => localStorage.getItem("fw_name") || "";
export const setDisplayName = (n) => localStorage.setItem("fw_name", n);
export const getFamilyCode = () => localStorage.getItem("fw_family") || "";
export const setFamilyCode = (c) =>
  c ? localStorage.setItem("fw_family", c) : localStorage.removeItem("fw_family");

// ---- Prayer Journal ----
export const getPrayers = () => read("fw_prayers", []);
export const addPrayer = (p) => {
  const list = getPrayers();
  const item = { id: uid(), created: Date.now(), answered: false, ...p };
  list.unshift(item);
  write("fw_prayers", list);
  return item;
};
export const updatePrayer = (id, patch) => {
  const list = getPrayers().map((p) => (p.id === id ? { ...p, ...patch } : p));
  write("fw_prayers", list);
};
export const deletePrayer = (id) =>
  write("fw_prayers", getPrayers().filter((p) => p.id !== id));

// ---- Sermon Notes ----
export const getSermons = () => read("fw_sermons", []);
export const addSermon = (s) => {
  const list = getSermons();
  const item = { id: uid(), created: Date.now(), ...s };
  list.unshift(item);
  write("fw_sermons", list);
  return item;
};
export const updateSermon = (id, patch) =>
  write("fw_sermons", getSermons().map((s) => (s.id === id ? { ...s, ...patch } : s)));
export const deleteSermon = (id) =>
  write("fw_sermons", getSermons().filter((s) => s.id !== id));

// ---- Verse Memorization (Leitner spaced repetition) ----
const INTERVALS = [0, 1, 2, 4, 8, 16]; // days per box level (1..5)
export const getMemoryVerses = () => read("fw_memory", []);
export const addMemoryVerse = (v) => {
  const list = getMemoryVerses();
  if (list.some((m) => m.ref === v.ref)) return null;
  const item = { id: uid(), box: 1, added: Date.now(), due: Date.now(), reviews: 0, ...v };
  list.unshift(item);
  write("fw_memory", list);
  return item;
};
export const deleteMemoryVerse = (id) =>
  write("fw_memory", getMemoryVerses().filter((m) => m.id !== id));
export const reviewMemoryVerse = (id, correct) => {
  const list = getMemoryVerses().map((m) => {
    if (m.id !== id) return m;
    const box = correct ? Math.min(5, m.box + 1) : 1;
    const due = Date.now() + INTERVALS[box] * 86400000;
    return { ...m, box, due, reviews: (m.reviews || 0) + 1, lastReviewed: Date.now() };
  });
  write("fw_memory", list);
};
export const getDueMemoryVerses = () =>
  getMemoryVerses().filter((m) => m.due <= Date.now());

// ---- Reading Plan progress ----
export const getPlanState = () => read("fw_plans", {}); // {planId: {startedAt, done:{dayIdx:true}}}
export const startPlan = (planId) => {
  const s = getPlanState();
  if (!s[planId]) s[planId] = { startedAt: Date.now(), done: {} };
  write("fw_plans", s);
};
export const togglePlanDay = (planId, dayIdx) => {
  const s = getPlanState();
  s[planId] = s[planId] || { startedAt: Date.now(), done: {} };
  if (s[planId].done[dayIdx]) delete s[planId].done[dayIdx];
  else s[planId].done[dayIdx] = true;
  write("fw_plans", s);
  return s;
};
export const markPlanDayDone = (planId, dayIdx) => {
  const s = getPlanState();
  s[planId] = s[planId] || { startedAt: Date.now(), done: {} };
  if (!s[planId].done[dayIdx]) {
    s[planId].done[dayIdx] = true;
    write("fw_plans", s);
  }
  return s;
};
export const planProgress = (planId, total) => {
  const s = getPlanState()[planId];
  const done = s ? Object.keys(s.done).length : 0;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0, started: !!s };
};

// ---- Devotional preferences ----
export const getDevoTopics = () => read("fw_devo_topics", ["faith", "peace", "hope"]);
export const setDevoTopics = (t) => write("fw_devo_topics", t);

// ---- Audio Bible preferences ----
export const getAudioPrefs = () => read("fw_audio", { rate: 0.95, voice: "", autoAdvance: true });
export const setAudioPrefs = (p) => write("fw_audio", p);
